import React, { createContext, useState, useEffect, ReactNode } from 'react';
import {
  SizingLevel,
  SIZING_SCALES,
  DEFAULT_SIZING_LEVEL,
} from '@/types/sizing';
import { getInitialPreferences } from '@/hooks/usePreferenceManager';
import { usePreferencesQuery } from '@/hooks/queries';
import { logger } from '@/utils/logger';

interface SizingContextValue {
  sizingLevel: SizingLevel;
  setSizingLevel: (level: SizingLevel) => void;
  resetSizing: () => void;
  multiplier: number;
}

// eslint-disable-next-line react-refresh/only-export-components -- exported context object alongside provider
export const SizingContext = createContext<SizingContextValue | undefined>(
  undefined
);

function applySizingMultiplier(multiplier: number): void {
  const root = document.documentElement;
  root.style.setProperty('--sizing-multiplier', multiplier.toString());
}

export const SizingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sizingLevel, setSizingLevelState] = useState<SizingLevel>(() => {
    // Read initial sizing through the preference owner's API (not storage directly)
    const storedSizing = getInitialPreferences()?.sizing as
      | SizingLevel
      | undefined;
    if (storedSizing && Object.values(SizingLevel).includes(storedSizing)) {
      return storedSizing;
    }
    return DEFAULT_SIZING_LEVEL;
  });

  useEffect(() => {
    const scale = SIZING_SCALES[sizingLevel];
    const root = document.documentElement;
    root.setAttribute('data-sizing', sizingLevel);
    applySizingMultiplier(scale.multiplier);
  }, [sizingLevel]);

  // Subscribe to preference changes via React Query cache
  const { data: queryPreferences } = usePreferencesQuery();

  useEffect(() => {
    const storedSizing = queryPreferences?.sizing as SizingLevel | undefined;
    if (storedSizing && Object.values(SizingLevel).includes(storedSizing)) {
      logger.ui.debug('[SizingContext] Updating sizing from query cache:', {
        sizing: storedSizing,
      });
      setSizingLevelState(storedSizing);
    }
  }, [queryPreferences]);

  const setSizingLevel = (level: SizingLevel) => {
    setSizingLevelState(level);
  };

  const resetSizing = () => {
    setSizingLevelState(DEFAULT_SIZING_LEVEL);
  };

  const multiplier = SIZING_SCALES[sizingLevel].multiplier;

  return (
    <SizingContext.Provider
      value={{ sizingLevel, setSizingLevel, resetSizing, multiplier }}
    >
      {children}
    </SizingContext.Provider>
  );
};
